import React, { useState } from 'react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { Trash2, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { FieldLabel, ErrorText } from '../canFrameDecoder/RawFrameCard';
import { spnColor, STATUS_STYLES } from '../../../lib/j1939FrameDecoder';

export const StatusBadge = ({ status, testId }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.invalid;
  return <Badge className={`gap-1 ${s.cls}`} data-testid={testId}>{s.label}</Badge>;
};

// One SPN definition row plus its decoded result. Fields wrap 2-up on a phone, 3-up on a
// tablet and 6-up on a desktop, so the dense editor stays usable at 375 px.
const SpnRow = ({ spn, k, result, onChange, onRemove, canRemove, tid }) => {
  const [open, setOpen] = useState(false);
  const fe = result?.fieldErrors || {};
  const decode = result?.decode;
  const color = spnColor(k);
  const id = `${tid}-spn-${k + 1}`;

  const field = (key, label, { tip, placeholder, inputMode, mono = true, wide = false } = {}) => (
    <div className={`space-y-1.5 min-w-0 ${wide ? 'col-span-2' : ''}`}>
      <FieldLabel htmlFor={`${id}-${key}`} tip={tip}>{label}</FieldLabel>
      <Input
        id={`${id}-${key}`} value={spn[key]} placeholder={placeholder} inputMode={inputMode}
        onChange={(e) => onChange({ [key]: e.target.value })}
        className={`h-9 ${mono ? 'font-mono' : ''}`} aria-invalid={!!fe[key]} data-testid={`${id}-${key}-input`}
        spellCheck={false} autoComplete="off"
      />
      {fe[key] && <ErrorText testId={`${id}-${key}-error`}>{fe[key]}</ErrorText>}
    </div>
  );

  const select = (key, label, options, tip) => (
    <div className="space-y-1.5 min-w-0">
      <FieldLabel tip={tip}>{label}</FieldLabel>
      <Select value={spn[key]} onValueChange={(v) => onChange({ [key]: v })}>
        <SelectTrigger className="h-9" data-testid={`${id}-${key}-select`} aria-label={label}><SelectValue /></SelectTrigger>
        {/* Fixed max height: stops Radix's popper re-measuring in a loop (ResizeObserver error). */}
        <SelectContent className="max-h-60">
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {fe[key] && <ErrorText testId={`${id}-${key}-error`}>{fe[key]}</ErrorText>}
    </div>
  );

  const auto = (which) => (result?.minMaxAuto?.[which] && result[which] !== null && result[which] !== undefined
    ? `auto (${result[which]})` : 'auto');

  return (
    <div className="rounded-md border border-structure bg-background p-3 space-y-3 min-w-0" data-testid={id}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="h-3.5 w-3.5 shrink-0 rounded-sm" style={{ background: color.bg }} aria-hidden="true" />
          <span className="font-chivo text-xs font-bold uppercase tracking-wider">SPN row {k + 1}</span>
          <span className="font-mono text-xs text-muted-foreground truncate">{spn.name || 'Unnamed'}{spn.spn !== '' ? ` · SPN ${spn.spn}` : ''}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {decode && <StatusBadge status={decode.status} testId={`${id}-status`} />}
          {result && !result.inDbc && <Badge variant="outline" className="text-muted-foreground">Not in DBC</Badge>}
          {canRemove && (
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onRemove} aria-label={`Remove SPN row ${k + 1}`} data-testid={`${id}-remove`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {field('name', 'Name', { placeholder: 'EngineSpeed', wide: true, tip: 'DBC signal name: letters, digits and _, not starting with a digit.' })}
        {field('spn', 'SPN', { placeholder: '190', inputMode: 'numeric', tip: 'Suspect Parameter Number, 0 to 524287. Written to the DBC as the SPN attribute.' })}
        {field('startBit', 'Start Bit', { inputMode: 'numeric', tip: 'DBC start bit (byte × 8 + bit, bytes counted from 0). J1939 documents count from 1: their byte 4 bit 1 is start bit 24. Intel: the LSB. Motorola: the MSB.' })}
        {field('length', 'Length (bits)', { inputMode: 'numeric', tip: '1 to 64 bits. J1939 range flags apply to 2, 4, 8, 16 and 32-bit unsigned SPNs.' })}
        {select('byteOrder', 'Byte Order', [
          { value: 'intel', label: 'Intel (LE)' },
          { value: 'motorola', label: 'Motorola (BE)' },
        ], 'Intel (little endian) is the J1939 byte order. Motorola (big endian) uses the DBC sawtooth numbering: the start bit is the MSB.')}
        {select('valueType', 'Signed / Unsigned', [
          { value: 'unsigned', label: 'Unsigned' },
          { value: 'signed', label: 'Signed' },
        ], "J1939 parameters are unsigned; negative ranges come from the offset. Signed (two's complement) is for proprietary PGNs and switches the J1939 range flags off.")}
        {field('factor', 'Factor', { inputMode: 'decimal', tip: 'Resolution per bit. Value = Raw × Factor + Offset. Must not be 0.' })}
        {field('offset', 'Offset', { inputMode: 'decimal' })}
        {field('min', 'Min', { inputMode: 'decimal', placeholder: auto('min'), tip: 'Blank = the J1939 valid-signal range (or the full raw range if none applies).' })}
        {field('max', 'Max', { inputMode: 'decimal', placeholder: auto('max'), tip: 'Blank = the J1939 valid-signal range (or the full raw range if none applies).' })}
        {field('unit', 'Unit', { placeholder: 'rpm', mono: false, tip: 'Printable ASCII without double quotes; write degC rather than °C.' })}
      </div>

      {decode && (
        <div className="rounded-md bg-muted/40 p-2.5 space-y-2" data-testid={`${id}-result`}>
          {decode.decoded ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-xs">
              <div className="min-w-0"><div className="text-[10px] uppercase text-muted-foreground">Value</div>
                <div className={`font-bold text-autoxium-blue ${decode.physical === null ? 'text-sm break-words' : 'text-base break-all'}`} data-testid={`${id}-value`}>
                  {decode.physicalText}{decode.unit && <span className="text-xs font-normal"> {decode.unit}</span>}
                </div>
              </div>
              <div className="min-w-0"><div className="text-[10px] uppercase text-muted-foreground">Raw</div>
                <div className="break-all" data-testid={`${id}-raw`}>{decode.raw}{decode.raw !== decode.rawUnsigned ? ` (unsigned ${decode.rawUnsigned})` : ''}</div></div>
              <div className="min-w-0"><div className="text-[10px] uppercase text-muted-foreground">Hex</div>
                <div className="break-all" data-testid={`${id}-hex`}>{decode.rawHex}</div></div>
              <div className="min-w-0"><div className="text-[10px] uppercase text-muted-foreground">J1939 range</div>
                <div className="break-words" data-testid={`${id}-range`}>{decode.range.applies ? decode.range.label : 'n/a'}</div></div>
            </div>
          ) : null}
          {decode.reasons.length > 0 && (
            <ul className="space-y-1">
              {decode.reasons.map((r) => (
                <li key={r} className="flex items-start gap-1.5 text-xs text-destructive"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {r}</li>
              ))}
            </ul>
          )}
          {decode.decoded && (
            <>
              <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 text-xs font-medium text-autoxium-blue" data-testid={`${id}-details-toggle`}>
                {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Details
              </button>
              {open && (
                <div className="space-y-3" data-testid={`${id}-details`}>
                  <ol className="space-y-1 font-mono text-xs list-decimal list-inside">
                    {decode.steps.map((s) => <li key={s} className="break-words">{s}</li>)}
                  </ol>
                  {decode.range.applies ? (
                    <Table className="text-xs">
                      <TableHeader>
                        <TableRow><TableHead className="h-8">Raw range</TableHead><TableHead className="h-8">Hex</TableHead><TableHead className="h-8">Meaning</TableHead></TableRow>
                      </TableHeader>
                      <TableBody>
                        {decode.range.bands.map((band) => (
                          <TableRow key={band.from} className={band.status === decode.range.status ? 'bg-autoxium-blue/10 font-semibold' : ''}>
                            <TableCell className="py-1.5 font-mono">{band.from === band.to ? band.from : `${band.from} – ${band.to}`}</TableCell>
                            <TableCell className="py-1.5 font-mono">{band.fromHex === band.toHex ? band.fromHex : `${band.fromHex} – ${band.toHex}`}</TableCell>
                            <TableCell className="py-1.5">{band.label}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-xs text-muted-foreground">{decode.range.reason}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SpnRow;
